// User profile image cropping before uploading
import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
// Import Cropper with type assertion to avoid TypeScript errors
// @ts-ignore
import Cropper from 'react-easy-crop';
import { Upload, ZoomIn, ZoomOut, Loader } from 'lucide-react';
import debug from 'debug';
import { Button } from '../../../components/ui/button';
import { Slider } from '../../../components/ui/slider';
import { cn } from '../../../lib/utils';

import getCroppedImg from './canvasUtils';

// Define Area type locally
interface Area {
  x: number;
  y: number;
  width: number;
  height: number;
}

const log = debug('log:ImageCropTool');

function readFile(file: Blob) {
  return new Promise<string | null>((resolve) => {
    const reader = new FileReader();
    reader.addEventListener(
      'load',
      () => resolve(reader.result?.toString() || null),
      false,
    );
    reader.readAsDataURL(file);
  });
}

const ImageCropTool = ({ onCrop }: { onCrop: (image: string) => void }) => {
  const { t } = useTranslation();
  const [imageSrc, setImageSrc] = React.useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showError, setShowError] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        if (imageSrc && croppedAreaPixels) {
          setIsProcessing(true);
          const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);

          if (croppedImage) {
            onCrop(croppedImage);
          }
          setIsProcessing(false);
        }
      } catch (e) {
        log(e);
        setIsProcessing(false);
      }
    })();
  }, [imageSrc, croppedAreaPixels, onCrop]);

  const onCropComplete = useCallback(
    (_croppedArea: Area, croppedAreaPx: Area) => {
      setCroppedAreaPixels(croppedAreaPx);
    },
    [],
  );

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrorMessage(
          t('profile.fileSizeTooLarge', 'File size must be less than 5MB'),
        );
        setShowError(true);
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        setErrorMessage(
          t('profile.invalidFileType', 'Please upload an image file'),
        );
        setShowError(true);
        return;
      }

      const imageDataUrl = await readFile(file);
      setImageSrc(imageDataUrl);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrorMessage(
          t('profile.fileSizeTooLarge', 'File size must be less than 5MB'),
        );
        setShowError(true);
        return;
      }

      if (file.type.startsWith('image/')) {
        const imageDataUrl = await readFile(file);
        setImageSrc(imageDataUrl);
      } else {
        setErrorMessage(
          t('profile.invalidFileType', 'Please upload an image file'),
        );
        setShowError(true);
      }
    }
  };

  const increaseZoom = () => {
    setZoom(Math.min(zoom + 0.1, 3));
  };

  const decreaseZoom = () => {
    setZoom(Math.max(zoom - 0.1, 1));
  };

  return (
    <div className="w-full">
      {imageSrc ? (
        <div className="relative w-full mb-2">
          <div className="relative h-[300px] overflow-hidden rounded-lg border border-gray-200 mb-4 bg-gray-50">
            {/* Use type assertion to avoid TypeScript errors */}
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
            {isProcessing && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/70 z-10">
                <Loader className="h-8 w-8 animate-spin text-gray-600" />
              </div>
            )}
          </div>

          <div className="mb-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700 min-w-[40px]">
                {t('profile.zoom', 'Zoom')}
              </span>
              <Button
                size="icon"
                variant="outline"
                onClick={decreaseZoom}
                className="h-8 w-8"
                disabled={zoom <= 1}
              >
                <ZoomOut className="h-4 w-4" />
              </Button>
              <Slider
                value={[zoom]}
                min={1}
                max={3}
                step={0.1}
                onValueChange={(value: number[]) => setZoom(value[0])}
                className="flex-1 mx-2"
              />
              <Button
                size="icon"
                variant="outline"
                onClick={increaseZoom}
                className="h-8 w-8"
                disabled={zoom >= 3}
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="text-center">
            <Button
              variant="outline"
              onClick={() => setImageSrc(null)}
              size="sm"
            >
              {t('profile.chooseDifferentImage', 'Choose Different Image')}
            </Button>
          </div>
        </div>
      ) : (
        <div
          className={cn(
            'border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 cursor-pointer',
            isDragging
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-300 hover:border-gray-400 bg-white',
          )}
          role="button"
          tabIndex={0}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => document.getElementById('file-input')?.click()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              document.getElementById('file-input')?.click();
            }
          }}
        >
          <Upload className="h-10 w-10 text-gray-400 mx-auto mb-4" />
          <p className="text-sm font-medium text-gray-700 mb-1">
            {t('profile.dragDropText', 'Click to upload or drag and drop')}
          </p>
          <p className="text-xs text-gray-500">
            {t('profile.supportedFormats', 'PNG, JPG, GIF up to 5MB')}
          </p>
          <input
            id="file-input"
            type="file"
            onChange={onFileChange}
            accept="image/*"
            className="hidden"
          />
        </div>
      )}

      {showError && errorMessage && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600">{errorMessage}</p>
        </div>
      )}
    </div>
  );
};

export default ImageCropTool;
